import {GamesMap, IGameImageSize, IGamesService, RecentlyPlayedGames} from './games.service.interface';
import {GameInfo} from './game-info';
import {action, observable} from 'mobx';
import {ServiceBase} from '../ServiceBase';
import {Check} from '@sparkware/app-utils';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';

export class GamesService extends ServiceBase implements IGamesService {

  constructor(serviceFactory: IServiceFactoryExtended) {
      super(serviceFactory);

    this.reaction(
      () => this.services.user.isAuthorized,
      () => this._updateGamesMap(),
      {
        fireImmediately: true
      });

    this.services.sdk.events.onRecentlyPlayedGamesUpdated(this._updateRecentlyPlayedGames);
  }




  @observable.ref gamesMap: GamesMap = {};
  @action.bound
  private _setGamesMap(gamesMap: GamesMap): void {
    this.gamesMap = gamesMap;
  }

  @observable.ref
  recentlyPlayedGames: RecentlyPlayedGames = {
      isInitialized: false,
      gameTypes: []
  };

  @action.bound
  private _updateRecentlyPlayedGames(gameTypes: number[]): void {
    if(!this.recentlyPlayedGames.isInitialized
        || !this.services.sdk.getUtils().arraysAreEqual(this.recentlyPlayedGames.gameTypes, gameTypes)) {
      this.recentlyPlayedGames = {
          isInitialized: true,
          gameTypes: gameTypes
      }
    }
  }



  private async _buildGamesMap(sdkGamesInfo: any): Promise<GamesMap> {

    let aboutTexts = await this._getGamesAboutTexts();

    let gamesMap: GamesMap = {};

    const usedGameTypesBySiteMap = this.services.configuration.usedGameTypesBySite.reduce((accumulator, gt) => {
        accumulator[gt] = true;
        return accumulator;
    }, {});

    Object.keys(sdkGamesInfo).forEach(gameType => {
      try {
        // filter only in-layout games
        if (usedGameTypesBySiteMap[gameType]) {
            let sdkInfo = sdkGamesInfo[gameType];
            let generalSettings = this.services.configuration.data.generalSettings;
            let gameTagInfo = (generalSettings && generalSettings.gamesSettings && generalSettings.gamesSettings.gamesTags && generalSettings.gamesSettings.gamesTags[gameType]) || {};

            // for now, rawGameData will contain also gameTagInfo properties - which are read by adminClient settings
            // moreover, we'll add a new property on it regarding allowSwitchToDemo
            let rawGameData = Object.assign(sdkInfo, gameTagInfo);
            let gameInfo = new GameInfo(rawGameData, this.services, aboutTexts[gameType]);
            gamesMap[gameInfo.gameType] = gameInfo;
        }
      } catch(err) {
        this.services.logger.error(`Failed to generate GameInfo for game type ${gameType}`, err);
      }

    });

    return gamesMap;
  }

  private _gamesAboutTexts: {[key: string]: string} | null = null;

  private async _getGamesAboutTexts(): Promise<{[key: string]: string}> {
    if(this._gamesAboutTexts) {
      return this._gamesAboutTexts;
    }
    let gamesAboutTextConfigPath = this.services.configuration.data.generalSettings.gamesSettings.gamesAboutTextPath;

    //if there is no about path (not supported in this regulation) - we prevent the http call
    if(!gamesAboutTextConfigPath){
      this._gamesAboutTexts = {};
      return this._gamesAboutTexts;
    }

    let gamesAboutTextUrl = '';

    try {
      gamesAboutTextUrl = gamesAboutTextConfigPath.replace('{currentLanguage}', this.services.language.currentLanguage);
      let aboutTextsJson = await this.services.http.get(gamesAboutTextUrl);

      if(Check.isNullOrUndefined(aboutTextsJson) || Object.keys(aboutTextsJson).length === 0) {
        this.services.logger.warning(`No games about texts returned from ${gamesAboutTextUrl}`);
      }
      this._gamesAboutTexts = aboutTextsJson || {};

      return this._gamesAboutTexts!;
    } catch (err) {
      if(err && ('statusCode' in err) && err.statusCode === 404) {
        // NB: Ignore 404s, usually for things like spa_about.json that actually shouldn't exist.
        return {};
      }
      this.services.logger.error(`Failed to read games about texts from ${gamesAboutTextUrl}`, err);
      return {};
    }


  }

  private async _updateGamesMap(): Promise<void> {
    let usedGameTypesBySite = this.services.configuration.usedGameTypesBySite || [];
    if(usedGameTypesBySite.length === 0) {
      this.services.logger.error(`configuration.usedGameTypesBySite returned empty array`);
      return;
    }

    const gamesMap = await this._getGamesInfoMap(usedGameTypesBySite);
    this._setGamesMap(gamesMap);
  }

  async _getGamesInfoMap(gameTypes: number[]): Promise<GamesMap> {
    try {
      let sdkGamesInfo = await this.services.sdk.getGamesInfo(gameTypes);
      return await this._buildGamesMap(sdkGamesInfo);
    } catch (err) {
      this.services.logger.error(`sdk.getGamesInfo failed in GamesService.getGamesInfo`, err);
      return {};
    }
  }

   async getGamesInfoMap(gameTypes: number[]): Promise<GamesMap> {
    if (!gameTypes || gameTypes.length === 0) {
        return {};
    }

    // fill data from cache (if cache found)
    if (Object.keys(this.gamesMap).length > 0) {
        const inCacheGames = gameTypes.filter((gt) => this.gamesMap[gt]);
        const result = inCacheGames.reduce((acc, gt) => {acc[gt] = this.gamesMap[gt]; return acc;}, {});
        return result;
    } else {
        // otherwise fill it from SDK
        return this._getGamesInfoMap(gameTypes);
    }
  }

  async getNotInLayoutGamesInfo(gameTypes: number[]): Promise<GamesMap> {
      let sdkGamesInfo = await this.services.sdk.getGamesInfo(gameTypes);
      let gamesMap: GamesMap = {};
      Object.keys(sdkGamesInfo).forEach(gameType => {
          let rawGameData = Object.assign(sdkGamesInfo[gameType], this.gamesMap[gameType]);
          gamesMap[gameType] = new GameInfo(rawGameData, this.services, '')
      });
      return gamesMap;
  }

  async getGamesInfoArray(gameTypes: number[]): Promise<GameInfo[]> {
    let gamesMap = await this.getGamesInfoMap(gameTypes);

    const result: GameInfo[] = [];
    for(let i = 0; i < gameTypes.length; i++) {
      const gameType = gameTypes[i];
      if (gamesMap[gameType]) {
        result.push(gamesMap[gameType]);
      }
    }
    return result;
  }

  async getGameInfo(gameType: number) {
    const gamesMap = await this.getGamesInfoMap([gameType]);
    if(gamesMap) {
      return gamesMap[gameType];
    } else {
      return null;
    }
  }

  async getCorrelatedGames(gameType: number, numberOfGames: number): Promise<number[]> {
    try {
      return await this.services.sdk.getCorrelatedGames(gameType, numberOfGames);
    } catch (err) {
      this.services.logger.error(`Failed to get from SDK the correlated games for game type ${gameType}`, err);
      return [];
    }
  }

  shouldUseSquareIcons(forceSquareIcons?: boolean) {
    return this.services.device.hybrid.isEmbedded || this.services.miniOrbit.isEnabled || forceSquareIcons;
  }

  getGameImageExpectedSizes(forceSquareIcons?: boolean, animated?: boolean): IGameImageSize {

    if (this.shouldUseSquareIcons(forceSquareIcons)) {
      if (animated) {
        return {
          width: 210,
          height: 292
        };
      }
      return {
        width: 400,
        height: 555
      };
    }

    return {
      width: 210,
      height: 210
    };
  }
}
